import {CurrentPageStore} from '@/store/content-optimizer/currentPage';
import {Progress, Tooltip, Popover} from 'antd';
import {observer} from 'mobx-react';
import {Dispatch, SetStateAction, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Table} from '@/components/common-components/components/index';
import styles from './style.module.scss';
import {numberWithCommas} from '@/utils/number';
import {SingleCompetitorType} from '@/store/content-optimizer/CurrentPageModel';
import {KeywordSelector} from './KeywordSelector';
import {useStore} from '@/store/root-store';
import {NextImg} from '@/utils/nextImg';


interface Props {
  store: CurrentPageStore;
  isSubscriber: boolean;
  activeKw: string;
  setActiveKw: Dispatch<SetStateAction <string>>;
  setCompetitors: (value: any) => void;
  competitors: any;
  filteredCompetitorsData: any;
  setFilteredCompetitorsData: (value: any) => void;
  schemaTypesList: any;
  setSchemaTypesList: (value: any) => void;
  allSchemasCount: number;
  setAllSchemasCount: (value: number) => void;
  nullCount: number;
  setNullCount: (value: number) => void;
  selectedSchema: any;
  setSelectedSchema: (value: any) => void;
}

export const CompetitorsTable = observer(({
  store,
  isSubscriber,
  activeKw,
  setActiveKw,
  setCompetitors,
  competitors,
  filteredCompetitorsData,
  setFilteredCompetitorsData,
  schemaTypesList,
  allSchemasCount,
  nullCount,
  selectedSchema,
  setSelectedSchema}: Props) => {
  const {contentOptimizer: {
    currentPage: {
      setShowCompetitorTermsTableDetail,
    }}} = useStore('');
  const [isSmallScreen, setIsSmallScreen] = useState(true);
  const [allowScroll, setAllowScroll] = useState(false);

  const onWindowResize = () => {
    setIsSmallScreen(window.innerWidth < 1441);
    setAllowScroll(window.innerWidth < 768);
  };

  useEffect(() => {
    onWindowResize();
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  // const addDefaultSrc = e => {
  //   e.target.src = '/img/world-wide-web.svg';
  // };

  const getProgressColor = (value: number, min: number, average: number, max?: number) => {
    if (value <= min) {
      return '#F44343';
    }
    if (value <= average && value > min) {
      return '#FF8536';
    }
    if (value > average) {
      if (max && value < max) {
        return '#BFE15E';
      }
      return '#52D477';
    }
    return '#52D477';
  };

  const reFormatSchemaType = value => {
    const outputString = value.replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
    return outputString;
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const {column, order} = sorter;
    const {sortField} = column || {};
    const data = [...competitors];
    if (sortField && order && data) {
      data.sort( (a, b) => {
        const _dir = order === 'ascend' ? 1 : -1;
        if (a[sortField] > b[sortField]) return 1 * _dir;
        if (a[sortField] < b[sortField]) return -1 * _dir;
        return 0;
      });
      setCompetitors(data);
    } else {
      setCompetitors(store?.analytics?.competitorsData?.filter(item => item.keyword === activeKw)[0]?.competitors);
    }
  };

  useEffect(() => {
    if (selectedSchema?.value == 'all-schemas') {
      setFilteredCompetitorsData(competitors);
    } else if (selectedSchema?.value == 'no-schemas') {
      setFilteredCompetitorsData(competitors?.filter(item => item?.schema?.length == 0 || item?.schema == null));
    } else {
      const selectedSchemaValue = selectedSchema?.value;

      const filteredItems = [];

      competitors?.forEach(item => {
        if (item?.schema) {
          if (Array.isArray(item?.schema)) {
            item?.schema?.forEach(ele => {
              if (ele?.graph) {
                if (Array.isArray(ele?.graph)) {
                  ele?.graph?.forEach(graphItem => {
                    if (Array.isArray(graphItem?.type)) {
                      if (graphItem?.type?.includes(selectedSchemaValue)) {
                        filteredItems.push(item);
                      }
                    } else if (graphItem?.type === selectedSchemaValue) {
                      filteredItems.push(item);
                    }
                  });
                }
              } else if (ele?.type === selectedSchemaValue) {
                filteredItems.push(item);
              }
            });
          }
        }
      });

      setFilteredCompetitorsData(filteredItems);
    }
  }, [selectedSchema]);

  const columns = [
    {
      title: () => <span>#</span>,
      dataIndex: 'position',
      key: 'position',
      sortField: 'position',
      width: 45,
      fixed: allowScroll ? false : 'left' as 'left',
      render: (_, record: SingleCompetitorType) => {
        return <span>{record?.position}</span>;
      },
    },
    {
      title: () => <span>{'PAGE / URL'.toUpperCase()}</span>,
      dataIndex: 'title',
      key: 'title',
      sortField: 'title',
      fixed: allowScroll ? false : 'left' as 'left',
      width: isSmallScreen ? 300 : 'auto',
      ellipsis: true,
      align: 'left' as 'left',
      render: (_, record: SingleCompetitorType) => {
        return <PageUrlWrapper>
          <span style={{cursor: 'pointer'}} className={styles.competitorIcon}><NextImg
          // onError={e => addDefaultSrc(e)}
            src={`https://www.google.com/s2/favicons?sz=16&domain_url=${record?.url}`} /></span>
          <Tooltip title={<> <p>{record?.title}</p>
            <a href={record?.url} target='_blank' rel='noreferrer'>{record?.url}</a></>}>
            <div className={styles.pageTitleUrl}>
              <span style={{cursor: 'pointer', width: isSmallScreen ? '240px' : 'auto', lineHeight: '16px', overflow: 'hidden', textOverflow: 'ellipsis'}}>{record?.title}</span>
              <a style={{width: isSmallScreen ? '240px' : 'auto', overflow: 'hidden', textOverflow: 'ellipsis'}} href={record?.url} target='_blank' rel='noreferrer'>{record?.url}</a>
            </div>
          </Tooltip></PageUrlWrapper>;
      },
    },
    {
      title: () => <span>{'DR'.toUpperCase()}</span>,
      dataIndex: 'dr',
      key: 'dr',
      sortField: 'dr',
      sorter: (a: any, b: any) => a.dr - b.dr,
      align: 'center' as 'center',
      width: 60,
      render: (_, record: SingleCompetitorType) => {
        const percent = record?.dr ? record?.dr % 1 === 0 ? record?.dr : parseFloat(record?.dr.toFixed(2)) : 0;
        return <DRWrapper>
          <div style={{width: '8px', height: '8px', backgroundColor: '#2D6CCA', borderRadius: '50%'}}></div>&nbsp;
          <div>{percent}</div>
        </DRWrapper>;
      },
    },
    {
      title: () => <span>{'CS'.toUpperCase()}</span>,
      dataIndex: 'contentScore',
      key: 'contentScore',
      sortField: 'contentScore',
      align: 'right' as 'right',
      sorter: (a: any, b: any) => a.contentScore - b.contentScore,
      width: 60,
      render: (_, record: SingleCompetitorType) => {
        const percent = record?.contentScore ? record?.contentScore % 1 === 0 ? record?.contentScore : parseFloat(record?.contentScore.toFixed(2)) : 0;
        return <div>
          <Progress
            percent={percent}
            // percent={Math.round(parseInt(percent))}
            strokeLinecap='round'
            gapDegree={0}
            trailColor='#ddd'
            strokeColor={getProgressColor( percent || 0, 25, 50, 75)}
            // strokeColor='#7F4EAD'
            showInfo={true}
            type='circle'
            width={30}
            strokeWidth={9}
            size='small'
            format={percent => <span style={{fontSize: '12px', color: '#000'}}>{percent }</span>}
          />
        </div>;
      },
    },
    {
      title: () => <span>{'Schema'.toUpperCase()}</span>,
      dataIndex: 'schema',
      key: 'schema',
      sortField: 'schema',
      align: 'center' as 'center',
      width: 150,
      render: (_, record: SingleCompetitorType) => {
        let schemaLength = 0;
        Array.isArray(record?.schema) && record?.schema?.map(item => {
          item?.type ? schemaLength += 1 : Array.isArray(item?.graph) && item?.graph?.map(graphItem => graphItem?.type ? schemaLength += 1 : '');
        });

        const schemaItemsList = [];
        Array.isArray(record?.schema) && record?.schema?.map(item => {
          item?.type ? schemaItemsList.push(item) : Array.isArray(item?.graph) && item?.graph?.map(graphItem => schemaItemsList.push(graphItem));
        });
        return <span
          style={{color: '#2D6CCA', cursor: 'pointer'}}>
          {record?.schema != null && record?.schema != undefined && Array.isArray(record?.schema) && record?.schema?.length != 0 ?
            <>
              <div style={{display: 'flex', justifyContent: 'center'}}>
                <BlueTextWithUnderline onClick={() => setShowCompetitorTermsTableDetail(true, record?.title, record?.url, 0, record?.schema[0])}>{reFormatSchemaType(String(record?.schema ? (record?.schema[0]?.graph ? record?.schema[0]?.graph[0]?.type : record?.schema[0]?.type) : '-'))}</BlueTextWithUnderline>
                {schemaLength > 1 &&
                <>
                  <div style={{cursor: 'default'}}>,&nbsp;</div>
                  <Popover
                    autoAdjustOverflow={true}
                    title=''
                    placement='bottom'
                    trigger='click'
                    content={<ULStyled>{schemaItemsList?.map((item, idx) =>
                      <>
                        {idx == 0 ? '' :
                          <div
                            onClick={() => setShowCompetitorTermsTableDetail(true, record?.title, record?.url, idx, item)}
                            style={{width: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#2D6CCA', cursor: 'pointer', paddingLeft: '20px'}}>
                            <li><BlueTextWithUnderline key={idx}>{reFormatSchemaType(String(item?.type))}</BlueTextWithUnderline></li>
                          </div>
                        }
                      </>,
                    )}</ULStyled>}>
                    <BlueTextWithUnderline>+{schemaLength - 1}</BlueTextWithUnderline>
                  </Popover>
                </>}
              </div>
            </> : '-'
          }
        </span>;
      },
    },
    {
      title: () => <><span>{'int'.toUpperCase()}</span><br/><span>{'links'.toUpperCase()}</span></>,
      dataIndex: 'internalLinks',
      key: 'internalLinks',
      sortField: 'internalLinks',
      align: 'center' as 'center',
      sorter: (a: any, b: any) => a?.internalLinks?.length - b?.internalLinks?.length,
      width: 80,
      render: (_, record: SingleCompetitorType) => {
        return <>
          {record?.internalLinks?.length == 0 ?
            <BlueTextWithUnderline>{numberWithCommas(record?.internalLinks?.length) || '-' }</BlueTextWithUnderline> :
            <Popover
              autoAdjustOverflow={true}
              overlayInnerStyle={{padding: '5px'}}
              content={
                <LinksContainer>
                  {record?.internalLinks?.length > 0 ? record?.internalLinks?.map((item, idx) =>
                    <ul key={idx} style={{maxWidth: '550px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#2D6CCA', paddingLeft: '20px', paddingRight: '20px', marginBottom: 0}}>
                      <li><LinkWrapper><StyledAnchor href={`${item}`} key={idx} rel='noreferrer' target='_blank'>{item}</StyledAnchor></LinkWrapper></li>
                    </ul>) : ''}
                </LinksContainer>}
              title=''
              placement='bottom'
              trigger='click'>
              <BlueTextWithUnderline>{numberWithCommas(record?.internalLinks?.length) || '-' }</BlueTextWithUnderline>
            </Popover>
          }
        </>;
      },
    },
    {
      title: () => <><span>{'ext'.toUpperCase()}</span><br/><span>{'links'.toUpperCase()}</span></>,
      dataIndex: 'externalLinks',
      key: 'externalLinks',
      sortField: 'externalLinks',
      align: 'center' as 'center',
      sorter: (a: any, b: any) => a?.externalLinks?.length - b?.externalLinks?.length,
      width: 80,
      render: (_, record: SingleCompetitorType) => {
        return <>
          {record?.externalLinks?.length == 0 ?
            <BlueTextWithUnderline>{numberWithCommas(record?.externalLinks?.length) || '-' }</BlueTextWithUnderline> :
            <Popover
              autoAdjustOverflow={true}
              overlayInnerStyle={{padding: '5px'}}
              content={
                <LinksContainer>
                  {record?.externalLinks?.length > 0 ? record?.externalLinks?.map((item, idx) =>
                    <ul key={idx} style={{maxWidth: '550px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#2D6CCA', paddingLeft: '20px', paddingRight: '20px', marginBottom: 0}}>
                      <li><LinkWrapper><StyledAnchor href={`${item}`} key={idx} rel='noreferrer' target='_blank'>{item}</StyledAnchor></LinkWrapper></li>
                    </ul>) : ''}
                </LinksContainer>}
              title=''
              placement='bottom'
              trigger='click'>
              <BlueTextWithUnderline>{numberWithCommas(record?.externalLinks?.length) || '-' }</BlueTextWithUnderline>
            </Popover>
          }
        </>;
      },
    },
    {
      title: () => <span>{'h1'.toUpperCase()}</span>,
      dataIndex: 'h1Count',
      key: 'h1Count',
      sortField: 'h1Count',
      align: 'center' as 'center',
      sorter: (a: any, b: any) => a?.h1Count - b?.h1Count,
      width: 50,
      render: (_, record: SingleCompetitorType) => {
        return <span>{numberWithCommas(record?.h1Count) || '-' }</span>;
      },
    },
    {
      title: () => <span>{'h2'.toUpperCase()}</span>,
      dataIndex: 'h2Count',
      key: 'h2Count',
      sortField: 'h2Count',
      align: 'center' as 'center',
      sorter: (a: any, b: any) => a?.h2Count - b?.h2Count,
      width: 50,
      render: (_, record: SingleCompetitorType) => {
        return <span>{numberWithCommas(record?.h2Count) || '-' }</span>;
      },
    },
    {
      title: () => <span>{'h3'.toUpperCase()}</span>,
      dataIndex: 'h3Count',
      key: 'h3Count',
      sortField: 'h3Count',
      align: 'center' as 'center',
      sorter: (a: any, b: any) => a?.h3Count - b?.h3Count,
      width: 50,
      render: (_, record: SingleCompetitorType) => {
        return <span>{numberWithCommas(record?.h3Count) || '-' }</span>;
      },
    },
    {
      title: () => <span>{'Word Count'.toUpperCase()}</span>,
      dataIndex: 'wordCount',
      key: 'wordCount',
      sortField: 'wordCount',
      align: 'center' as 'center',
      sorter: (a: any, b: any) => a?.wordCount - b?.wordCount,
      width: 115,
      render: (_, record: SingleCompetitorType) => {
        return <span>{numberWithCommas(record?.wordCount) || '-' }</span>;
      },
    },
  ];

  return <Wrapper>
    <Header>
      <KeywordSelector isCompetitorsTable={true} isSubscriber={isSubscriber} setCompetitors={setCompetitors} setFilteredCompetitorsData={setFilteredCompetitorsData} setActiveKw={setActiveKw} activeKw={activeKw}/>
    </Header>
    <TypesSchemaFilterWrapper>
      <SchemaFilterTitle>Types of Schema Found:</SchemaFilterTitle>
      <SchemaFilterWrapper>
        <SchemaFilter selectedSchema={selectedSchema?.idx == 'all-schemas' ? true : false} onClick={() => setSelectedSchema({idx: 'all-schemas', value: 'all-schemas'})}>All schemas&nbsp;<div style={{color: '#A3A4A4'}}>|</div>&nbsp;{allSchemasCount}</SchemaFilter>
        <SchemaFilter selectedSchema={selectedSchema?.idx == 'no-schemas' ? true : false} onClick={() => setSelectedSchema({idx: 'no-schemas', value: 'no-schemas'})}>No schemas&nbsp;<div style={{color: '#A3A4A4'}}>|</div>&nbsp;{nullCount}</SchemaFilter>
        {schemaTypesList?.map((item, idx) => {
          return <SchemaFilter selectedSchema={selectedSchema?.idx == `${idx}` ? true : false} onClick={() => setSelectedSchema({idx: `${idx}`, value: item?.value})} key={idx}>{reFormatSchemaType(String(item?.value))}&nbsp;<div style={{color: '#A3A4A4'}}>|</div>&nbsp;{item?.count}</SchemaFilter>;
        })}
      </SchemaFilterWrapper>
    </TypesSchemaFilterWrapper>
    <div className={styles.tableOuterPages}>
      <Table
        style={{paddingLeft: '10px', paddingRight: '10px', marginTop: '0px !important'}}
        pagination={false}
        columns={columns}
        scroll={{x: 600, y: 600}}
        dataSource={filteredCompetitorsData}
        onChange={handleTableChange}
        // loading={isPagesInit}
        className={styles.table}
        loadingRows={10}
      />
    </div>

  </Wrapper>;
});

const Wrapper = styled.div`
  padding: 30px;
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const DRWrapper = styled.div`
  background-color: rgba(45, 108, 202, 0.07);
  display: flex;
  border-radius: 6px;
  align-items: center;
  justify-content: center;
  width: 55px;
  height: 32px;
  padding: 7px 12px 8px 12px;
`;

const PageUrlWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TypesSchemaFilterWrapper = styled.div`

`;

const SchemaFilterTitle = styled.div`
  color: #4E5156;
  font-size: 14px;
  margin-left: 10px;
  margin-bottom: 10px;
`;

const SchemaFilterWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  margin-left: 10px;
  width: 97%;
  overflow-x: auto;
  &::-webkit-scrollbar {
  width: 0;
  height: 0;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
  }
`;

const SchemaFilter = styled.div<{selectedSchema?: boolean}>`
  min-width: fit-content;
  min-height: 28px;
  border-radius: 14px;
  background: ${p => p?.selectedSchema ? 'linear-gradient(0deg, rgba(127, 78, 173, 0.20) 0%, rgba(127, 78, 173, 0.20) 100%), #FFF' : '#FFFF'};
  box-shadow: 1px -2px 20px 0px rgba(0, 0, 0, 0.06);
  border: ${p => p?.selectedSchema ? '1px solid #7F4EAD' : '1px solid #E8E8E8'};
  display: flex;
  padding: 5px 10px 6px 10px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const LinksContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
    background-color: #F5F5F5;
  }

  &::-webkit-scrollbar {
    width: 5px;
    background-color: #F5F5F5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0,0,0,0.28);
    border-radius: 10px;
  }
`;

const LinkWrapper = styled.div`
  width: auto;
  font-size: 12px;
  line-height: 14px;
  color: rgb(45, 108, 202);
  white-space: pre-line;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
`;

const StyledAnchor = styled.a`
  text-decoration: none;
  transition: text-decoration 0.2s;

  &:hover {
    text-decoration: underline;
  }
`;

const BlueTextWithUnderline = styled.div`
  color: #2D6CCA;
  cursor: pointer;
  text-decoration: none;
  transition: text-decoration 0.2s;

  &:hover {
    text-decoration: underline;
  }
`;

const ULStyled = styled.ul`
  padding-left: 0px;
  max-height: 160px;
  overflow-y: auto;
  padding: 5px;
  margin-bottom: 0;
  width: 200px;

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
    background-color: #F5F5F5;
  }

  &::-webkit-scrollbar {
    width: 5px;
    background-color: #F5F5F5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0,0,0,0.28);
    border-radius: 10px;
  }
`;
